/**
 * A switch of the certification currency, as the software applies it.
 *
 * Each entry of UNCONVERTED_OPEN_ITEMS is a value without a currency that
 * closure code reads or that becomes a certified document's amount. While
 * the list is not empty, a day a stored switch covers is refused
 * (`assertSwitchApplicable`); an item leaves the list with the code that
 * converts it.
 *
 * `preSwitchDay` names the day whose currency a value recorded before any
 * switch carries.
 */
import { ApplicationError } from "../errors/index.js";
import { frenchDay } from "../utils/french-day.js";
import { currencyDay } from "./certification-currency.js";
/** Values without a currency a certified document is computed from, in French, as the refusal names them. */
export const OPEN_ITEM_LABELS = Object.freeze({
    ARTICLE_PRICES: "les prix des articles",
    STOCK_COSTS: "le coût moyen pondéré des stocks et le coût des mouvements de stock",
    QUOTES: "les devis",
    RECURRING_INVOICES: "les factures récurrentes",
    LOYALTY_POINTS: "la valeur et le barème des points de fidélité",
});
/**
 * The values of OPEN_ITEM_LABELS a switch would leave unconverted. Article
 * prices, stock costs, quotes and recurring invoices convert at the official
 * rate with the company's open-item conversion (suite,
 * CurrencyOpenItemConversion). The value of a loyalty point (often 1 franc)
 * and the amount that earns one have no counterpart in a currency worth
 * hundreds of francs at the two decimals and the whole number their columns
 * hold.
 */
export const UNCONVERTED_OPEN_ITEMS = Object.freeze(["LOYALTY_POINTS"]);
/** The code of the refusal of a day a switch the software cannot apply covers. */
export const CURRENCY_SWITCH_REFUSAL_CODE = "BASCULE_NON_CONVERTIBLE";
/**
 * The code of the refusal of a new document on a day a switch covers while
 * the company has not converted its open items at the official rate.
 */
export const OPEN_ITEMS_CONVERSION_REQUIRED_CODE = "CONVERSION_ELEMENTS_OUVERTS_REQUISE";
/** `items` named in one French phrase: « a, b et c ». */
export function listOpenItems(items) {
    const labels = items.map((item) => OPEN_ITEM_LABELS[item]);
    return labels.length === 1 ? labels[0] : `${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}`;
}
/**
 * Every switch of `jurisdiction` in force on the day of `date`, in order:
 * each active period starting on or before that day whose currency differs
 * from the one of the active period before it. A period that keeps the
 * currency in force is not a switch.
 */
export function switchesInForce(periods, jurisdiction, date) {
    const day = currencyDay(date);
    const active = periods
        .filter((period) => period.jurisdiction === jurisdiction && period.withdrawnAt == null)
        .map((period) => ({ currency: period.currency, day: currencyDay(period.effectiveFrom) }))
        .filter((period) => period.day <= day)
        .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
    const switches = [];
    for (let i = 1; i < active.length; i++) {
        if (active[i].currency !== active[i - 1].currency)
            switches.push(active[i]);
    }
    return switches;
}
/** The latest switch of `jurisdiction` in force on the day of `date`, or null. */
export function switchInForce(periods, jurisdiction, date) {
    const switches = switchesInForce(periods, jurisdiction, date);
    return switches[switches.length - 1] ?? null;
}
/** The `YYYY-MM-DD` day before `day`. */
function dayBefore(day) {
    const at = new Date(`${day}T00:00:00Z`);
    at.setUTCDate(at.getUTCDate() - 1);
    return at.toISOString().slice(0, 10);
}
/**
 * The day whose certification currency a value recorded before any switch
 * carries, seen on the day of `date`: that day when no switch is in force
 * on it, else the day before the first switch in force.
 */
export function preSwitchDay(periods, jurisdiction, date) {
    const first = switchesInForce(periods, jurisdiction, date)[0];
    return first === undefined ? currencyDay(date) : dayBefore(first.day);
}
/**
 * Refuses the certification currency of the day of `date` when a switch is
 * in force on it while UNCONVERTED_OPEN_ITEMS is not empty: no document is
 * issued in the new currency from amounts stored in the old one. The days
 * before the switch are unaffected.
 */
export function assertSwitchApplicable(periods, jurisdiction, date) {
    if (UNCONVERTED_OPEN_ITEMS.length === 0)
        return;
    const inForce = switchInForce(periods, jurisdiction, date);
    if (inForce === null)
        return;
    throw new ApplicationError(`Le changement de monnaie de certification vers ${inForce.currency} au ${frenchDay(inForce.day)} ne peut pas être appliqué : le logiciel ne sait pas encore convertir au taux officiel ${listOpenItems(UNCONVERTED_OPEN_ITEMS)}. Aucune facture ni vente n'est émise à partir de cette date.`, CURRENCY_SWITCH_REFUSAL_CODE, 409);
}
//# sourceMappingURL=certification-currency-switch.js.map